import React, { FC, Fragment, ReactNode, useCallback } from 'react';
import merge from 'lodash/merge';
import styled from 'styled-components';
import set from 'lodash/set';
import get from 'lodash/get';
import find from 'lodash/find';
import { useDispatch } from 'react-redux';

import analytics from '@ad-scripts/lib/analytics';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { DefaultIvrConfiguration } from 'src/models/Ivrs/Ivr';
import EmbedModal, { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import generateAlphaNumericId from 'src/presentation/components/pages/ivr/helpers/generateAlphaNumericId';
import InputField from '@ad-scripts/presentation/components/common/input_field';
import ivrsResource from 'src/actions/ivrs/ivrsResource';

const TITLE = 'Create New IVR';
const DESCRIPTION = 'Add a name for this IVR';
const SUBMIT_TEXT = 'Create';

const EditForm = ({ attr, formContext }) => {
  const { errors, updateState, workingState, resetError } = formContext;
  const onChange = useCallback(
    val => {
      resetError({ attr, errors });
      updateState(set({}, attr, val));
    },
    [attr, errors, resetError, updateState]
  );
  const v = `${get(workingState, attr)}`;
  const error = find(errors, error => error.attr === attr);
  return (
    <FormContainer>
      <Form context={formContext}>
        <InputIvrNameContainer>
          <InputField
            attr="name"
            autoFocus={true}
            data-aid={`name`}
            error={get(error, 'detail')}
            formContext={formContext}
            onChange={onChange}
            placeholder="Type IVR Name"
            value={v}
          />
        </InputIvrNameContainer>
      </Form>
    </FormContainer>
  );
};

const InputIvrNameContainer = styled.div`
  align-items: center;
  display: flex;
  position: relative;

  [data-aid='name-error'] {
    position: absolute;
    top: 35px;
  }
`;

type AddNewIvrProps = {
  onSubmit?: Function;
  onCancel?: Function;
  errors: any[];
  title?: string;
  submitText?: string;
  description?: string | ReactNode;
};

const AddNewIvrModal: FC<AddNewIvrProps> = ({
  errors,
  title = TITLE,
  description = DESCRIPTION,
  submitText = SUBMIT_TEXT,
  onCancel,
  onSubmit,
}) => {
  const { resetErrors } = ivrsResource.actions;
  const { validate } = ivrsResource.effects;

  const initializedItem = merge({}, DefaultIvrConfiguration, { id: generateAlphaNumericId() });
  const dispatch = useDispatch();

  const handleCancel = () => {
    analytics.track('Admin - Add New IVR - cancel');
    dispatch(resetErrors({}));
    onCancel && onCancel();
  };
  const formContext = useForm({
    errors,
    item: initializedItem,
    onSave: onSubmit,
    resetErrors,
    validate,
  });

  const { onSave } = formContext;

  return (
    <StyledEmbedModal
      data-aid="new-ivr-modal"
      title={title}
      content={EmbedModalContent({ context: formContext, description, submitText, onCancel: handleCancel, onSave })}
      onCancel={handleCancel}
      modalSize={ModalSize.LARGE}
    />
  );
};

const EmbedModalContent = ({ context, description, submitText, onCancel, onSave }) => {
  return (
    <Fragment>
      <DescriptionContainer>{description}</DescriptionContainer>
      <EditForm attr={'name'} formContext={context} />
      <StyledFooter>
        <Button data-aid={'editor-cancel'} type={Button.Types.TEXT} onClick={onCancel}>
          Cancel
        </Button>
        <Button data-aid={'editor-save'} onClick={onSave}>
          {submitText}
        </Button>
      </StyledFooter>
    </Fragment>
  );
};

const FormContainer = styled.div`
  padding: 20px 0 20px 0;
  text-align: left;
`;

const StyledFooter = styled(Footer)`
  justify-content: center;
`;

const DescriptionContainer = styled.div`
  text-align: center;
`;

const StyledEmbedModal = styled(EmbedModal)`
  .Modal-backdrop {
    background-color: ${p => p.theme.colors.white};
    opacity: 0.8;
  }

  .modalCard-content {
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.04), 0px 0px 10px rgba(0, 0, 0, 0.06), 0px 0px 80px rgba(0, 0, 0, 0.06);
    padding: 0 45px;
    width: 500px;
  }
`;

export default AddNewIvrModal;
